import {
  Button,
  TableBody,
  TableCell,
  TablePagination,
  TableContainer,
  Grid,
  Divider,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import Table from '@mui/material/Table';
import Tooltip from '@mui/material/Tooltip';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import LaunchIcon from '@mui/icons-material/Launch';
import * as moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import {
  ACTIONTYPES,
  COLORS,
  DIALOGTYPES,
  DIALOG,
  LABEL,
  TOOLTIPS,
  BaseRoleCodes,
} from '../../theme/constants';
import { CRPTableTheme, CRPTheme, StyledTableRow } from '../../theme/theme';
import CenterTableCell from '../atoms/CenterTableCell';
import CrpCheckbox from '../atoms/CrpCheckbox';
import ContextMenu from '../molecules/ContextMenu';
import TableHead from '../molecules/ContainerTableHead';
import ContainerToolbar from '../molecules/ContainerToolbar';
import PickupDialog from './PickupDialog';
import YesNoDialog from './YesNoDialog';
import queryString from 'query-string';
import TerminalActionDialog from './TerminalActionDialog';
import FormikDialog from './formikDialog';
import addReasonSchema from 'modules/shipping-line/schemas/addReasonSchema';
import { hasAnyBaseRole, hasBaseRole } from 'auth/helper';
import PermissionGuard from 'auth/components/permissionGuard';

const styles = (theme) => ({
  tableRow: {
    height: 40,
  },
  hoverRemoveIcon: {
    '&:hover': {
      backgroundColor: `${COLORS.RED} !important`,
      cursor: 'pointer',
      '& > svg': {
        color: 'white !important',
      },
    },
  },
  checkbox: {
    padding: 8,
    margin: '0 4px',
    value: 'secondary',
    color: 'primary',
  },
  nextMode: {
    fill: '#606060',
  },
  content: {
    width: '100%',
  },
  responsiveCell: {
    maxWidth: '0',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    width: '100%',
  },
  contentBig: {
    flex: 5,
    paddingLeft: theme.spacing(3),
    width: '100%',
  },
  icons: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  iconText: {
    margin: '0px',
    marginTop: '5px',
    fontSize: '11px',
    color: COLORS.GREY,
  },
  wideCell: {
    padding: '20px 15px',
  },
  errorText: {
    fontSize: '0.875rem',
    color: 'red',
  },
});

const statusLogo = (status, classes) => {
  switch (status) {
    case 'Green':
      return (
        <div className={classes.icons}>
          <FiberManualRecordIcon fontSize="medium" htmlColor={'#4AB55F'} />
        </div>
      );
    case 'Red':
      return (
        <div className={classes.icons}>
          <FiberManualRecordIcon fontSize="medium" htmlColor={'#e60000'} />
        </div>
      );
    case 'Orange':
    case 'Yellow':
      return (
        <div className={classes.icons}>
          <FiberManualRecordIcon fontSize="medium" htmlColor={'#f57b00'} />
        </div>
      );
    default:
      return (
        <div className={classes.icons}>
          <FiberManualRecordIcon fontSize="medium" htmlColor={'#4AB55F'} />
        </div>
      );
  }
};

const formatExternalDatabrokerTooltip = (container) => {
  if (container.dataBrokerName && container.dataBrokerName.length !== 0) {
    return LABEL.MANAGED_EXTERNALLY + container.dataBrokerName;
  } else {
    return '';
  }
};

const ContainerTable = (props) => {
  const {
    classes,
    columns,
    paging,
    items,
    headerTitle,
    containers,
    archive,
    acceptReleases,
    declineReleases,
    releaseContainers,
    blockContainers,
    revokeContainers,
    revokeContainerPickups,
    loadContainersRowsPerPage,
    loadContainersPage,
    detailContainers,
    selected,
    loadSelectedSucceeded,
    selectContainersForScan,
    extendedToolbar,
    disableAccept,
  } = props;

  const [selectedPickupContainer, setSelectedPickupContainer] = React.useState(null);
  const navigate = useNavigate();
  const [terminalActionOpen, setTerminalActionOpen] = React.useState(false);
  const [activeItem, setItem] = React.useState('');
  const [container, setContainer] = React.useState(null);
  const [reasonDialogOpen, setReasonDialogOpen] = React.useState(false);

  const handleSelects = (newSelected) => {
    loadSelectedSucceeded(newSelected);
  };

  const handlePickupCallback = (container) => {
    setSelectedPickupContainer(container);
  };

  const forwardClick = (e, container) => {
    e.stopPropagation();
    handleSelects([container]);
    navigate('/containers/company');
  };

  const disableAcceptComponent = (action) => {
    return !action.isEnabled && disableAccept;
  };

  const acceptComponent = (container, disabled) => {
    return (
      <PermissionGuard permission="CAN_EDIT">
        <YesNoDialog
          callback={() => {
            handleSelects([]);
            acceptReleases({
              containers: [container],
              actionType: ACTIONTYPES.ACCEPT,
            });
          }}
          disabled={disabled}
          className={`${classes.wideCell}`}
          dialogType={DIALOGTYPES.ACCEPT}
          buttonTitle={LABEL.ACCEPT_LABEL}
          dialogTitle={DIALOG.ACCEPT_TITLE}
          contentText={DIALOG.ACCEPT_CONTENT}
        />
      </PermissionGuard>
    );
  };

  const declineComponent = (container, disabled) => {
    return (
      <PermissionGuard permission="CAN_EDIT">
        <YesNoDialog
          callback={() => {
            handleSelects([]);
            declineReleases({
              containers: [container],
              actionType: ACTIONTYPES.DECLINE,
            });
          }}
          disabled={disabled}
          className={`${classes.wideCell}`}
          dialogType={DIALOGTYPES.DECLINE}
          buttonTitle={LABEL.DECLINE_LABEL}
          dialogTitle={DIALOG.DECLINE_TITLE}
          contentText={DIALOG.DECLINE_CONTENT}
        />
      </PermissionGuard>
    );
  };

  const revokeComponent = (container, disabled, item) => {
    return (
      <PermissionGuard permission="CAN_EDIT">
        <YesNoDialog
          callback={() => {
            handleSelects([]);
            setItem(item);
            setContainer(container);
            setReasonDialogOpen(true);
          }}
          className={`${classes.wideCell}`}
          dialogType={DIALOGTYPES.REVOKE}
          buttonTitle={LABEL.REVOKE_LABEL}
          dialogTitle={DIALOG.REVOKE_NEXT_PARTY_TITLE}
          contentText={DIALOG.REVOKE_NEXT_PARTY_CONTENT}
          disabled={disabled}
        />
      </PermissionGuard>
    );
  };

  const revokePickupComponent = (container, disabled) => {
    return (
      <PermissionGuard permission="CAN_EDIT">
        <YesNoDialog
          callback={() => {
            handleSelects([]);
            revokeContainerPickups([container]);
          }}
          className={`${classes.wideCell}`}
          dialogType={DIALOGTYPES.REVOKE}
          buttonTitle={LABEL.REVOKEPICKUP_LABEL}
          dialogTitle={DIALOG.REVOKE_NEXT_PARTY_TITLE}
          contentText={DIALOG.REVOKE_NEXT_PARTY_CONTENT}
          disabled={disabled}
        />
      </PermissionGuard>
    );
  };

  const pickupComponent = (container, disabled) => {
    return (
      <PermissionGuard permission="CAN_EDIT">
        <PickupDialog
          callback={() => {
            handlePickupCallback(container);
          }}
          buttonTitle={LABEL.PICKUP_BUTTON}
          disabled={disabled}
          className={`${classes.wideCell}`}
          container={container}
          selectedContainer={selectedPickupContainer}
        />
      </PermissionGuard>
    );
  };

  const blockComponent = (container, disabled) => {
    return (
      <PermissionGuard permission="CAN_EDIT">
        <YesNoDialog
          callback={() => {
            blockContainers({
              containers: [container],
              actionType: ACTIONTYPES.BLOCK,
            });
          }}
          className={`${classes.wideCell}`}
          dialogType={DIALOGTYPES.BLOCK}
          buttonTitle={LABEL.BLOCK_LABEL}
          dialogTitle={DIALOG.BLOCK_TITLE}
          contentText={DIALOG.BLOCK_CONTENT}
          disabled={disabled}
        />
      </PermissionGuard>
    );
  };

  const releaseComponent = (container, disabled) => {
    return (
      <PermissionGuard permission="CAN_EDIT">
        <YesNoDialog
          callback={() => {
            releaseContainers({
              containers: [container],
              actionType: ACTIONTYPES.RELEASE,
            });
          }}
          className={`${classes.wideCell}`}
          dialogType={DIALOGTYPES.RELEASE}
          buttonTitle={LABEL.RELEASE_LABEL}
          dialogTitle={DIALOG.RELEASE_TITLE}
          contentText={DIALOG.RELEASE_CONTENT}
          disabled={disabled}
        />
      </PermissionGuard>
    );
  };

  const forwardComponent = (container, disabled) => {
    return (
      <Tooltip title={formatExternalDatabrokerTooltip(container)}>
        <span>
          <PermissionGuard permission="CAN_EDIT">
            <Button
              disabled={disabled}
              variant="text"
              color="primary"
              className={`${classes.wideCell}`}
              onClick={(e) => forwardClick(e, container)}
            >
              {LABEL.TRANSFER}
            </Button>
          </PermissionGuard>
        </span>
      </Tooltip>
    );
  };

  const normalComponent = (container, disabled) => {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <PermissionGuard permission="CAN_EDIT">
          <Button
            disabled={disabled}
            onClick={(e) => e.stopPropagation()}
            variant="text"
            color="primary"
            className={`${classes.wideCell}`}
          >
            {LABEL.NORMAL_LABEL}
          </Button>
        </PermissionGuard>
      </div>
    );
  };

  const pausedComponent = (container, disabled) => {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <PermissionGuard permission="CAN_EDIT">
          <Button
            disabled={disabled}
            onClick={(e) => e.stopPropagation()}
            variant="text"
            color="primary"
            className={`${classes.wideCell}`}
          >
            {LABEL.PAUSED_LABEL}
          </Button>
        </PermissionGuard>
      </div>
    );
  };

  const pauseComponent = (container, disabled) => {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <PermissionGuard permission="CAN_EDIT">
          <Button
            disabled={disabled}
            onClick={(e) => e.stopPropagation()}
            variant="text"
            color="primary"
            className={`${classes.wideCell}`}
          >
            {LABEL.PAUSE_LABEL}
          </Button>
        </PermissionGuard>
      </div>
    );
  };

  const pendingComponent = (disabled) => {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <PermissionGuard permission="CAN_EDIT">
          <Button
            disabled={disabled}
            onClick={(e) => e.stopPropagation()}
            variant="text"
            color="primary"
            className={`${classes.wideCell}`}
          >
            {LABEL.PENDING_LABEL}
          </Button>
        </PermissionGuard>
      </div>
    );
  };

  const transferredComponent = (disabled) => {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <PermissionGuard permission="CAN_EDIT">
          <Button
            disabled={disabled}
            onClick={(e) => e.stopPropagation()}
            variant="text"
            color="primary"
            className={`${classes.wideCell}`}
          >
            {LABEL.TRANSFERRED_LABEL}
          </Button>
        </PermissionGuard>
      </div>
    );
  };

  const selectForScanComponent = (container, disabled) => {
    return (
      <PermissionGuard permission="CAN_EDIT">
        <YesNoDialog
          callback={() => {
            selectContainersForScan({
              containers: [container],
              actionType: ACTIONTYPES.SCANNING,
            });
          }}
          className={`${classes.wideCell}`}
          dialogType={DIALOGTYPES.RELEASE}
          buttonTitle={LABEL.SELECT_FOR_SCAN_LABEL}
          dialogTitle={DIALOG.SELECT_FOR_SCAN_TITLE}
          contentText={DIALOG.SELECT_FOR_SCAN_CONTENT}
          disabled={disabled}
        />
      </PermissionGuard>
    );
  };

  const waitingForPickupComponent = (disabled, text, container) => {
    return (
      <Tooltip title={formatExternalDatabrokerTooltip(container)}>
        <span>
          <PermissionGuard permission="CAN_EDIT">
            <Button
              disabled={disabled}
              onClick={(e) => e.stopPropagation()}
              variant="text"
              color="primary"
              className={`${classes.wideCell}`}
            >
              {text}
            </Button>
          </PermissionGuard>
        </span>
      </Tooltip>
    );
  };

  const actionComponents = (container, maxActions) => {
    const labels = container.releaseRightActions.map((action, index) => {
      if (index >= maxActions) return null;

      switch (action.label.toUpperCase()) {
        case LABEL.PENDING_LABEL:
          return <CenterTableCell key={index} text={pendingComponent(!action.isEnabled)} />;
        case LABEL.TRANSFERRED_LABEL:
          return <CenterTableCell key={index} text={transferredComponent(!action.isEnabled)} />;
        case LABEL.TRANSFER.toUpperCase():
          return (
            <CenterTableCell key={index} text={forwardComponent(container, !action.isEnabled)} />
          );
        case LABEL.ACCEPT_LABEL:
          return (
            <CenterTableCell
              key={index}
              text={acceptComponent(container, disableAcceptComponent(action))}
            />
          );
        case LABEL.DECLINE_LABEL:
          return (
            <CenterTableCell key={index} text={declineComponent(container, !action.isEnabled)} />
          );
        case LABEL.PICKUP_LABEL:
          return (
            <CenterTableCell key={index} text={pickupComponent(container, !action.isEnabled)} />
          );
        case LABEL.REVOKE_LABEL:
          return (
            <CenterTableCell
              key={index}
              text={revokeComponent(container, !action.isEnabled, action.label)}
            />
          );
        case LABEL.REVOKEPICKUP_LABEL:
          return (
            <CenterTableCell
              key={index}
              text={revokePickupComponent(container, !action.isEnabled)}
            />
          );
        case LABEL.BLOCK_LABEL:
          return (
            <CenterTableCell key={index} text={blockComponent(container, !action.isEnabled)} />
          );
        case LABEL.RELEASE_LABEL:
          return (
            <CenterTableCell key={index} text={releaseComponent(container, !action.isEnabled)} />
          );
        case LABEL.NORMAL_LABEL:
          return (
            <CenterTableCell key={index} text={normalComponent(container, !action.isEnabled)} />
          );
        case LABEL.PAUSED_LABEL:
          return (
            <CenterTableCell key={index} text={pausedComponent(container, !action.isEnabled)} />
          );
        case LABEL.PAUSE_LABEL:
          return (
            <CenterTableCell key={index} text={pauseComponent(container, !action.isEnabled)} />
          );
        case LABEL.SELECT_FOR_SCAN_LABEL:
          return (
            <CenterTableCell
              key={index}
              text={selectForScanComponent(container, !action.isEnabled)}
            />
          );
        case LABEL.WAITING_FOR_PICKUP:
          return (
            <CenterTableCell
              key={index}
              text={waitingForPickupComponent(
                !action.isEnabled,
                LABEL.WAITING_FOR_PICKUP,
                container
              )}
            />
          );
        case LABEL.WAITING_FOR_RELEASE:
          return (
            <CenterTableCell
              key={index}
              text={waitingForPickupComponent(
                !action.isEnabled,
                LABEL.WAITING_FOR_RELEASE,
                container
              )}
            />
          );
        default:
          return <CenterTableCell key={index} />;
      }
    });

    let emptyCells = [];
    for (let i = container.releaseRightActions.length; i < maxActions; i++) {
      emptyCells.push(<CenterTableCell key={i + 10000} />);
    }

    return (
      <>
        {labels}
        {emptyCells}
      </>
    );
  };

  const handleChangePage = (event, page) => {
    loadContainersPage(page);
  };

  const handleChangeRowsPerPage = (event) => {
    loadContainersRowsPerPage(event.target.value);
  };

  const handleSelectAllClick = (event) => {
    const selectedItems = event.target.checked
      ? selected.concat(items.filter((el) => !selected.map((s) => s.id).includes(el.id)))
      : selected.filter((sid) => !items.map((i) => i.id).includes(sid.id));
    handleSelects(selectedItems);
  };

  const handleSingleClick = (container) => {
    const selectedItems = selected.map((e) => e.id).includes(container.id)
      ? selected.filter((el) => el.id !== container.id)
      : selected.concat(container);
    handleSelects(selectedItems);
  };

  const checkAllSelected = () => {
    const mappedItems = items
      .filter((x) => selected.map((el) => el.id).includes(x.id))
      .map((el) => el.id);
    return (
      mappedItems.sort.toString() === selected.map((el) => el.id).sort.toString() &&
      mappedItems.length === items.length &&
      items.length !== 0
    );
  };

  const handleCloseDialog = (e) => {
    e.stopPropagation();
    setTerminalActionOpen(false);
  };

  const handleActiveItemCallback = (e, item) => {
    e.stopPropagation();
    setItem(item);
    switch (item.toUpperCase()) {
      case LABEL.BLOCK_LABEL:
      case LABEL.RELEASE_LABEL:
      case LABEL.SELECT_FOR_SCAN_LABEL:
        setTerminalActionOpen(true);
        break;
      default:
        break;
    }
  };

  const performReleaseRightAction = (reason) => {
    switch (activeItem.toUpperCase()) {
      case LABEL.REVOKE_LABEL:
        revokeContainers({
          containers: [container],
          reason: reason,
          actionType: ACTIONTYPES.REVOKE,
        });
        break;
      default:
        break;
    }
  };

  const handleSubmitReasonDialog = (values) => {
    let reason = values.reason;
    performReleaseRightAction(reason);
    setReasonDialogOpen(false);
  };

  const handleCloseReasonDialog = () => {
    setReasonDialogOpen(false);
  };

  return (
    <ThemeProvider theme={detailContainers ? CRPTheme : CRPTableTheme}>
      <div>
        <ContainerToolbar
          selectedPickupContainer={selectedPickupContainer}
          pickupCallback={() => {
            handlePickupCallback(selected[0]);
          }}
          selected={selected}
          acceptReleases={acceptReleases}
          declineReleases={declineReleases}
          revokeContainers={revokeContainers}
          revokeContainerPickups={revokeContainerPickups}
          handleSelects={handleSelects}
          numItems={items.length}
          className={classes.wideCell}
          numSelected={selected.length}
          headerTitle={headerTitle}
          containers={containers}
          extended={extendedToolbar}
        />
        <Divider />
        <TableContainer component={Grid}>
          <Table aria-labelledby="tableTitle" style={{ minWidth: 650 }}>
            <TableHead
              className={classes.wideCell}
              columns={columns}
              onSelectAllClick={handleSelectAllClick}
              selectAllChecked={checkAllSelected()}
              checkboxes={!detailContainers && !archive}
              centerAlign={true}
            />
            <TableBody>
              {items.map((container) => {
                const qs = {
                  equipmentNumber: container.equipmentNumber,
                  billOfLadingNumbers: container.billOfLadingNumbers,
                  releaseIdentification: container.releaseIdentification,
                  releaseId: container.releaseId,
                };
                const detailLink =
                  (containers ? '/containers' : '/releases') +
                  '/detail?' +
                  queryString.stringify(qs);
                return (
                  <StyledTableRow
                    hover={!detailContainers && !archive}
                    tabIndex={-1}
                    key={container.id}
                    className={`${classes.tableRow}`}
                  >
                    {!detailContainers && !archive && (
                      <TableCell>
                        <CrpCheckbox
                          checked={selected.map((el) => el.id).includes(container.id)}
                          onChange={() => handleSingleClick(container)}
                        />
                      </TableCell>
                    )}
                    {detailContainers && (
                      <CenterTableCell
                        className={classes.hoverRemoveIcon}
                        onClick={() => handleSingleClick(container)}
                        text={<DeleteForeverIcon htmlColor={COLORS.GREY} />}
                      />
                    )}
                    <CenterTableCell text={'[' + container.billOfLadingNumbers.join(', ') + ']'} />
                    <CenterTableCell text={container.equipmentNumber} />
                    {!hasBaseRole(BaseRoleCodes.SHIP_AGENT_CODE) && (
                      <CenterTableCell text={container.originatorName} />
                    )}
                    {!hasAnyBaseRole([
                      BaseRoleCodes.SHIP_AGENT_CODE,
                      BaseRoleCodes.TERMINAL_CODE,
                    ]) && <CenterTableCell text={container.releaseFromName} />}
                    {(archive || hasAnyBaseRole([BaseRoleCodes.SHIP_AGENT_CODE])) && (
                      <CenterTableCell text={container.releaseToName} />
                    )}
                    <CenterTableCell
                      text={
                        <span
                          style={
                            moment(container.startDate).isAfter(moment())
                              ? { color: 'green' }
                              : null
                          }
                        >
                          {moment(container.startDate).format('DD/MM/YYYY  HH:mm')}
                        </span>
                      }
                    />
                    <CenterTableCell
                      text={
                        <span
                          style={
                            container.expirationDateTimeUtc != null &&
                            moment(container.expirationDateTimeUtc).isAfter(moment()) &&
                            container.releaseLightStatus === 'Ok'
                              ? { color: 'green' }
                              : { color: 'red' }
                          }
                        >
                          {container.expirationDateTimeUtc == null
                            ? ''
                            : moment(container.expirationDateTimeUtc).format('DD/MM/YYYY  HH:mm')}
                        </span>
                      }
                    />
                    {!hasBaseRole(BaseRoleCodes.TERMINAL_CODE) && (
                      <CenterTableCell text={container.terminalName} />
                    )}
                    {hasBaseRole(BaseRoleCodes.TRANSPORT_OPERATOR_CODE) && (
                      <CenterTableCell text={container.alfapassNumberMask} />
                    )}
                    {(containers || detailContainers) && (
                      <CenterTableCell
                        text={statusLogo(container.combinedReleaseLightStatus, classes)}
                      />
                    )}

                    {!detailContainers && !archive && actionComponents(container, 1)}
                    {!detailContainers && !archive && (
                      <CenterTableCell
                        text={
                          <PermissionGuard permission="CAN_EDIT">
                            <ContextMenu
                              classes={classes}
                              container={container}
                              loadSelectedSucceeded={loadSelectedSucceeded}
                              declineReleases={declineReleases}
                              acceptReleases={acceptReleases}
                              revokeContainers={revokeContainers}
                              revokeContainerPickups={revokeContainerPickups}
                              releaseContainers={releaseContainers}
                              selectContainersForScan={selectContainersForScan}
                              blockContainers={blockContainers}
                              pickupCallback={() => {
                                handlePickupCallback(container);
                              }}
                              selectedPickupContainer={selectedPickupContainer}
                              setActiveItemCallback={handleActiveItemCallback}
                              setContextMenuContainer={setContainer}
                            />
                          </PermissionGuard>
                        }
                      />
                    )}
                    {!detailContainers && !archive && (
                      <CenterTableCell
                        text={
                          <Tooltip title={TOOLTIPS.OPEN_DETAIL}>
                            <Link to={detailLink}>
                              <Button variant="text" color="primary" className={classes.wideCell}>
                                <LaunchIcon />
                              </Button>
                            </Link>
                          </Tooltip>
                        }
                      />
                    )}
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {paging && (
          <TablePagination
            component="div"
            count={paging.count}
            rowsPerPage={paging.rowsPerPage}
            page={paging.page}
            rowsPerPageOptions={[25, 50, 100]}
            backIconButtonProps={{
              'aria-label': LABEL.PREVIOUS_PAGE,
            }}
            nextIconButtonProps={{
              'aria-label': LABEL.NEXT_PAGE,
            }}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
        <TerminalActionDialog
          terminalActionOpen={terminalActionOpen}
          activeItem={activeItem}
          container={container}
          onClose={handleCloseDialog}
        />
        <FormikDialog
          initialValues={{ reason: '' }}
          schema={addReasonSchema}
          open={reasonDialogOpen}
          handleSubmit={handleSubmitReasonDialog}
          handleClose={handleCloseReasonDialog}
          dialogTitle={DIALOG.PROVIDE_REASON_TITLE}
          dialogContentText={DIALOG.PROVIDE_REASON_CONTENT}
          classes={classes}
          columnSize={12}
        />
      </div>
    </ThemeProvider>
  );
};

ContainerTable.propTypes = {
  classes: PropTypes.object,
  columns: PropTypes.array.isRequired,
  items: PropTypes.array.isRequired,
  companies: PropTypes.array,
  selected: PropTypes.array.isRequired,
  releaseContainers: PropTypes.func,
  blockContainers: PropTypes.func,
  loadContainersPage: PropTypes.func,
  loadContainersRowsPerPage: PropTypes.func,
  revokeContainers: PropTypes.func,
  declineReleases: PropTypes.func,
  acceptReleases: PropTypes.func,
  revokeContainerPickups: PropTypes.func,
  loadSelectedSucceeded: PropTypes.func,
  selectContainersForScan: PropTypes.func,
  detailContainers: PropTypes.bool,
  containers: PropTypes.bool,
  archive: PropTypes.bool,
  extended: PropTypes.bool,
  extendedToolbar: PropTypes.bool,
  headerTitle: PropTypes.string,
  paging: PropTypes.object,
};

export default withStyles(ContainerTable, styles);
